import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackButton from '../../../../core/design-system/components/BackButton';
import { Icon } from '../../../../core/design-system/components/Icon';
import AccountSettingsPage from '../AccountSettingsPage';
import FeedPreferencesPage from '../FeedPreferencesPage';
import AppearanceSettingsPage from '../AppearanceSettingsPage';
import PrivacySettingsPage from '../PrivacySettingsPage';
// NotificationSettingsPage retiré pour beta GPT Store (RAS-41)
import './styles.css';

const SettingsPage = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState(null);
  const [hasActiveSubSection, setHasActiveSubSection] = useState(false);

  const settingsItems = useMemo(() => [
    {
      id: 'account',
      label: t('items.account'),
      component: AccountSettingsPage
    },
    {
      id: 'feed',
      label: t('items.feed'),
      component: FeedPreferencesPage
    },
    {
      id: 'appearance',
      label: t('items.appearance'),
      component: AppearanceSettingsPage
    },
    {
      id: 'privacy',
      label: t('items.privacy'),
      component: PrivacySettingsPage
    },
    // Notifications retiré pour beta GPT Store (RAS-41)
  ], [t]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sectionParam = params.get('section');
    
    // Support ancien paramètre 'preferences' redirigé vers 'feed'
    // RAS-41: 'notifications' supprimé, rediriger vers la liste principale
    if (sectionParam === 'preferences') {
      setCurrentSection('feed');
      navigate('/dashboard/settings?section=feed', { replace: true });
    } else if (sectionParam === 'notifications') {
      // Section supprimée (RAS-41), retour à la liste
      setCurrentSection(null);
      setHasActiveSubSection(false);
      navigate('/dashboard/settings', { replace: true });
    } else if (sectionParam && settingsItems.some(item => item.id === sectionParam)) {
      setCurrentSection(sectionParam);
    }
  }, [location.search, settingsItems, navigate]);

  const handleBack = () => {
    if (currentSection) {
      if (hasActiveSubSection) {
        return;
      }
      setCurrentSection(null);
      navigate('/dashboard/settings', { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handleSubSectionChange = (isActive) => {
    setHasActiveSubSection(isActive);
  };

  const renderMainList = () => (
    <div className="settings-list">
      {settingsItems.map((item) => (
        <button
          key={item.id}
          className="settings-item"
          onClick={() => setCurrentSection(item.id)}
        >
          <div className="settings-item-content">
            <span>{item.label}</span>
          </div>
          <Icon name="chevron-right" size={20} className="settings-item-arrow" />
        </button>
      ))}
    </div>
  );

  return (
    <div className="settings-page">
      {currentSection && !hasActiveSubSection && (
        <header className="settings-header">
          <BackButton onClick={handleBack} />
          <h1 className="settings-title">
            {t(`items.${currentSection}`)}
          </h1>
        </header>
      )}
      <div className="settings-content">
        {currentSection ? (
          React.createElement(
            settingsItems.find(item => item.id === currentSection)?.component,
            { 
              hideHeader: true,
              onSubSectionChange: handleSubSectionChange
            }
          )
        ) : (
          renderMainList()
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
